// ActivityLogsTab — chronological audit feed embedded in /reports.
//
// Reuses the same `GET /api/v1/activity-log` endpoint as Settings → Audit
// Log; renders the same filters + table inline (no page chrome) so the
// outer ReportsPage tab strip provides the context.

import { useState } from 'react';
import { Card } from '../../../../components/ui/Card/Card';
import { EmptyState } from '../../../../components/ui/EmptyState/EmptyState';
import { Pagination } from '../../../../components/ui/Pagination/Pagination';
import { History } from 'lucide-react';
import { useAuditLog } from '../audit-log/hooks/useAuditLog';
import { useStaffList } from '../../../admin/users/hooks/useUsers';
import type { ActivityLogEntry } from '../../../../types/api';
import styles from './ActivityLogsTab.module.css';

const ENTITY_OPTIONS = [
  '',
  'booking',
  'receipt',
  'invoice',
  'customer',
  'driver',
  'staff',
  'pending',
  'bacs_run',
  'quickbooks',
] as const;

const DEFAULT_LIMIT = 50;

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function ActivityLogsTab() {
  const [action, setAction] = useState('');
  const [staffId, setStaffId] = useState('');
  const [entityType, setEntityType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  // Pull a slim staff list for the actor dropdown — adminless tenants
  // still get the dropdown but with limited entries.
  const staffQuery = useStaffList({ page: 1, limit: 100, status: 'all' });
  const staffNames = new Map<number, string>();
  for (const s of staffQuery.data?.staff ?? []) {
    staffNames.set(s.id, s.name);
  }

  const filters = {
    page,
    limit: DEFAULT_LIMIT,
    ...(action ? { action } : {}),
    ...(staffId ? { staff_id: Number(staffId) } : {}),
    ...(entityType ? { entity_type: entityType } : {}),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  };

  const list = useAuditLog(filters);
  const logs = list.data?.logs ?? [];
  const pagination = list.data?.pagination;
  const total = pagination?.total ?? 0;
  const totalPages = pagination?.totalPages ?? 1;

  function setFilter<T extends string>(setter: (v: T) => void, value: T) {
    setter(value);
    setPage(1);
  }

  function actorLabel(entry: ActivityLogEntry): string {
    if (entry.staff_id !== null) {
      return staffNames.get(entry.staff_id) ?? `Staff #${entry.staff_id}`;
    }
    if (entry.driver_id !== null) return `Driver #${entry.driver_id}`;
    return 'System';
  }

  return (
    <div className={styles.tab}>
      <div className={styles.filters} role="region" aria-label="Activity log filters">
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="al-date-from">
            Date From
          </label>
          <input
            id="al-date-from"
            type="date"
            className={styles.input}
            value={dateFrom}
            onChange={(e) => setFilter(setDateFrom, e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="al-date-to">
            Date To
          </label>
          <input
            id="al-date-to"
            type="date"
            className={styles.input}
            value={dateTo}
            onChange={(e) => setFilter(setDateTo, e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="al-action">
            Action
          </label>
          <input
            id="al-action"
            type="text"
            className={styles.input}
            placeholder="e.g. booking_created"
            value={action}
            onChange={(e) => setFilter(setAction, e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="al-staff">
            Actor
          </label>
          <select
            id="al-staff"
            className={styles.select}
            value={staffId}
            onChange={(e) => setFilter(setStaffId, e.target.value)}
          >
            <option value="">All actors</option>
            {(staffQuery.data?.staff ?? []).map((s) => (
              <option key={s.id} value={String(s.id)}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="al-entity">
            Entity Type
          </label>
          <select
            id="al-entity"
            className={styles.select}
            value={entityType}
            onChange={(e) => setFilter(setEntityType, e.target.value)}
          >
            {ENTITY_OPTIONS.map((opt) => (
              <option key={opt || 'all'} value={opt}>
                {opt === '' ? 'All entities' : opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card
        flush
        title="Activity Log"
        actions={
          <span className={styles.cardMeta}>
            {total} entr{total === 1 ? 'y' : 'ies'}
            {totalPages > 1 ? ` · Page ${page} of ${totalPages}` : ''}
          </span>
        }
      >
        {logs.length === 0 ? (
          <EmptyState
            icon={History}
            title="No audit entries"
            description="Adjust your filters or check a wider date range."
          />
        ) : (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>Entity</th>
                    <th>Actor</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDateTime(entry.created_at)}</td>
                      <td>
                        <span className={styles.actionPill}>{entry.action}</span>
                      </td>
                      <td>
                        {entry.details ?? <span className={styles.muted}>—</span>}
                      </td>
                      <td>
                        {entry.entity_type ? (
                          <span>{entry.entity_type}</span>
                        ) : (
                          <span className={styles.muted}>—</span>
                        )}
                      </td>
                      <td>{actorLabel(entry)}</td>
                      <td>
                        {entry.ip_address ?? <span className={styles.muted}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 ? (
              <div className={styles.paginationWrap}>
                <Pagination
                  pagination={{
                    page,
                    limit: pagination?.limit ?? DEFAULT_LIMIT,
                    total,
                    totalPages,
                  }}
                  onPageChange={setPage}
                />
              </div>
            ) : null}
          </>
        )}
      </Card>
    </div>
  );
}
